import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import TransparentLogo from '../shared/components/TransparentLogo';
import defaultLogo from '../shared/assets/logo.png';
import { useAuth } from '../shared/context/AuthContext';
import { useCart } from '../shared/context/CartContext';
import { useWishlist } from '../shared/context/WishlistContext';
import { resolvePostAuthPath } from '../shared/utils/authRedirect';
import ValidatedInput, { validateFields } from '../../validations/ValidatedInput';
import {
  sanitizeByKind,
  isIdentifier,
  isPassword,
  LIMITS,
} from '../../validations';
import './AccountPage.css';

export default function AccountPage() {
  const { user, isLoggedIn, login, register, logout } = useAuth();
  const { addItem, refresh: refreshCart } = useCart();
  const { refresh: refreshWishlist } = useWishlist();
  const location = useLocation();
  const navigate = useNavigate();
  const authRedirectRef = useRef({
    from: location.state?.from,
    pendingCart: location.state?.pendingCart,
  });
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    identifier: '',
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.notice || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.notice) setInfo(location.state.notice);
    if (location.state?.from || location.state?.pendingCart) {
      authRedirectRef.current = {
        from: location.state.from,
        pendingCart: location.state.pendingCart,
      };
    }
  }, [location.state]);

  const finishAuth = async () => {
    const { from, pendingCart } = authRedirectRef.current || {};
    await Promise.all([refreshCart(), refreshWishlist()]);

    if (pendingCart?.productId) {
      try {
        await addItem(
          { _id: pendingCart.productId },
          pendingCart.size || '',
          pendingCart.quantity || 1
        );
      } catch {
        // Still send the shopper to cart even if the add fails.
      }
    }

    const next = resolvePostAuthPath({ from, pendingCart });
    if (next) navigate(next, { replace: true });
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onIdentifierChange = (e) => {
    const next = sanitizeByKind('identifier', e.target.value);
    setForm((f) => ({ ...f, identifier: next }));
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (mode === 'login') {
      const idErr = isIdentifier(form.identifier);
      const pwErr = isPassword(form.password);
      if (idErr || pwErr) {
        setError(idErr || pwErr);
        return;
      }
    } else {
      const errs = validateFields([
        { kind: 'text', value: form.name, required: true, label: 'Name' },
        { kind: 'phone', value: form.phone, required: true },
        { kind: 'email', value: form.email, required: true },
        { kind: 'password', value: form.password, required: true },
      ]);
      if (errs.length) {
        setError(errs[0]);
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.identifier || form.email, form.password);
      } else {
        await register({
          name: form.name,
          email: form.email,
          phone: form.phone,
          password: form.password,
        });
      }
      await finishAuth();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <Navbar />
      <main className="account">
        {isLoggedIn ? (
          <div className="account__card">
            <TransparentLogo src={defaultLogo} alt="NAYAVARNAM" className="account__logo" />
            <p className="account__eyebrow">Signed in</p>
            <h1 className="account__name">{user.name}</h1>
            <p className="account__email">{user.email}</p>
            {user.phone && <p className="account__email">{user.phone}</p>}
            <button type="button" className="account__submit account__submit--ghost" onClick={logout}>
              Logout
            </button>
          </div>
        ) : (
          <div className="account__card">
            <TransparentLogo src={defaultLogo} alt="NAYAVARNAM" className="account__logo" />
            <p className="account__eyebrow">Your account</p>

            <div className="account__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className={mode === 'login' ? 'is-active' : ''}
                onClick={() => switchMode('login')}
              >
                Login
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'register'}
                className={mode === 'register' ? 'is-active' : ''}
                onClick={() => switchMode('register')}
              >
                Register
              </button>
            </div>

            <form onSubmit={onSubmit} className="account__form">
              {mode === 'register' && (
                <>
                  <label className="account__field">
                    <span>Full name</span>
                    <ValidatedInput
                      kind="text"
                      name="name"
                      placeholder="Your name"
                      value={form.name}
                      onChange={onChange}
                      autoComplete="name"
                      required
                    />
                  </label>
                  <label className="account__field">
                    <span>Phone (10 digits)</span>
                    <ValidatedInput
                      kind="phone"
                      name="phone"
                      placeholder="9876543210"
                      value={form.phone}
                      onChange={onChange}
                      autoComplete="tel"
                      required
                    />
                  </label>
                  <label className="account__field">
                    <span>Email</span>
                    <ValidatedInput
                      kind="email"
                      name="email"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={onChange}
                      autoComplete="email"
                      required
                    />
                  </label>
                  <label className="account__field">
                    <span>Password</span>
                    <ValidatedInput
                      kind="password"
                      name="password"
                      placeholder={`Min. ${LIMITS.passwordMin} characters`}
                      value={form.password}
                      onChange={onChange}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                </>
              )}

              {mode === 'login' && (
                <>
                  <label className="account__field">
                    <span>Email or phone</span>
                    <input
                      name="identifier"
                      placeholder="you@email.com or 10-digit phone"
                      value={form.identifier}
                      onChange={onIdentifierChange}
                      autoComplete="username"
                      required
                      maxLength={LIMITS.text}
                    />
                  </label>
                  <label className="account__field">
                    <span>Password</span>
                    <ValidatedInput
                      kind="password"
                      name="password"
                      placeholder="Password"
                      value={form.password}
                      onChange={onChange}
                      autoComplete="current-password"
                      required
                    />
                  </label>
                </>
              )}

              {info && <p className="account__info">{info}</p>}
              {error && <p className="account__error">{error}</p>}

              <button type="submit" className="account__submit" disabled={loading}>
                {loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            <p className="account__hint">
              {mode === 'login'
                ? 'Sign in with your email or 10-digit phone.'
                : 'Adding to bag requires an account. Register to save your bag and wishlist.'}
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
